import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { getAuthToken, clearAuthToken } from '../utils/auth';
import { logout } from '../reducers/user';
import * as api from '../services/api';
import TopBarBrand from './TopBarBrand';
import AppVersionBadge from './AppVersionBadge';
import { ReactComponent as LogoDeep } from '../assets/logos/icon-deep.svg';
import { ReactComponent as LogoNeuroIQ } from '../assets/logos/icon-neuroiq.svg';
import { useBrand } from '../hooks/useBrand';

// ─── Palette ───────────────────────────────────────────────────────────────
const C = {
  bg: '#EEF4FF',           // page background — light airy blue
  sidebar: '#FFFFFF',
  topbar: '#FFFFFF',
  border: '#E2EAF4',
  textPrimary: '#1A2744',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  activeGrad: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 50%, #2563EB 100%)',
  activeText: '#FFFFFF',
  activeShadow: '0 4px 14px rgba(14,165,233,0.30)',
  hover: '#F0F8FF',
  hoverText: '#0EA5E9',
};

// ─── SVG Icons ─────────────────────────────────────────────────────────────
const HomeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const FlaskIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3h6v7l3 10H6L9 10V3z" />
    <path d="M7 19h10" />
    <path d="M9 3H6" />
    <path d="M15 3h3" />
  </svg>
);
const ClipboardIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);
const ActivityIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const ScanIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="7" width="18" height="10" rx="2" />
    <path d="M7 3v4" />
    <path d="M17 3v4" />
    <path d="M7 17v4" />
    <path d="M17 17v4" />
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);
const ChatBubbleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);
const CommentIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const BellIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const GearIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const SignOutIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const GolfIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="12" y1="2" x2="12" y2="17" />
    <path d="M12 2 l5 3.5 -5 3.5" fill="currentColor" stroke="none" opacity="0.7"/>
    <ellipse cx="12" cy="20" rx="4" ry="1.5" />
  </svg>
);
const FootballIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <ellipse cx="12" cy="12" rx="9" ry="5.5" transform="rotate(-35 12 12)" />
    <line x1="7" y1="9" x2="17" y2="15" />
    <line x1="9.5" y1="7.5" x2="9.5" y2="16.5" />
    <line x1="14.5" y1="7.5" x2="14.5" y2="16.5" />
  </svg>
);
const BaseballIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M9 3.5 C8 7 8 17 9 20.5" />
    <path d="M15 3.5 C16 7 16 17 15 20.5" />
  </svg>
);
const HockeyIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M5 4 L5 17 Q5 21 10 21 L14 21" />
    <line x1="2" y1="4" x2="11" y2="4" />
    <ellipse cx="18" cy="21" rx="3" ry="1.5" />
  </svg>
);
const HamburgerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// ─── Layout ────────────────────────────────────────────────────────────────
const Layout = styled.div`
  display: flex;
  min-height: 100vh;
  flex-direction: row;
  background: ${C.bg};
`;

/** className hooks for @media print (e.g. clinical marketing Export → PDF) */
const SHELL_CLS = {
  layout: 'drschmoe-app-layout',
  sidebar: 'drschmoe-app-sidebar',
  rightArea: 'drschmoe-app-right-area',
  topBar: 'drschmoe-app-topbar',
  main: 'drschmoe-app-main',
  userOverlay: 'drschmoe-app-user-overlay',
  userPanel: 'drschmoe-app-user-panel',
} as const;

const MobileOverlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 199;
    opacity: ${(p) => (p.$open ? 1 : 0)};
    pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
    transition: opacity 0.25s ease;
  }
`;

// ─── Sidebar ───────────────────────────────────────────────────────────────
const Sidebar = styled.nav<{ $mobileOpen?: boolean }>`
  width: 240px;
  flex-shrink: 0;
  background: ${C.sidebar};
  display: flex;
  flex-direction: column;
  padding: 0 0 12px;
  border-right: 1px solid ${C.border};
  box-shadow: 2px 0 16px rgba(14, 165, 233, 0.05);
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 200;
    transform: translateX(${(p) => (p.$mobileOpen ? '0' : '-100%')});
    transition: transform 0.25s ease;
    box-shadow: ${(p) => (p.$mobileOpen ? '4px 0 24px rgba(0,0,0,0.15)' : 'none')};
  }
`;

const SidebarLogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid ${C.border};
  margin-bottom: 12px;
`;

const rockLogo = keyframes`
  0%   { transform: perspective(300px) rotateY(-32deg) rotateX(5deg); }
  50%  { transform: perspective(300px) rotateY(32deg)  rotateX(-5deg); }
  100% { transform: perspective(300px) rotateY(-32deg) rotateX(5deg); }
`;

const LogoPill = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(145deg, #ffffff 0%, #e8f0ff 100%);
  border: 1px solid ${C.border};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${rockLogo} 6s ease-in-out infinite;
  box-shadow:
    inset 2px 2px 6px rgba(255,255,255,0.9),
    inset -2px -2px 6px rgba(100,130,200,0.15),
    0 2px 8px rgba(37,99,235,0.12);
`;


const SidebarBrandName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${C.textPrimary};
  letter-spacing: -0.01em;
`;

const SidebarSection = styled.div`
  padding: 0 12px;
`;

const SidebarSectionLabel = styled.div`
  padding: 10px 8px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${C.textMuted};
`;

const SidebarLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  font-size: 13.5px;
  font-weight: 500;
  color: ${C.textSecondary};
  text-decoration: none;
  border-radius: 10px;
  margin-bottom: 2px;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${C.hover};
    color: ${C.hoverText};
  }
  &.active {
    background: ${C.activeGrad};
    color: ${C.activeText};
    box-shadow: ${C.activeShadow};
  }
  & svg {
    flex-shrink: 0;
    opacity: 0.7;
  }
  &.active svg {
    opacity: 1;
  }
`;

const SidebarDivider = styled.div`
  height: 1px;
  background: ${C.border};
  margin: 8px 20px;
`;

const SidebarSpacer = styled.div`
  flex: 1;
  min-height: 16px;
`;

// ─── TopBar ────────────────────────────────────────────────────────────────
const RightArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 28px;
  background: ${C.topbar};
  border-bottom: 1px solid ${C.border};
  flex-shrink: 0;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 260px;
  height: 38px;
  padding: 0 14px;
  background: ${C.bg};
  border-radius: 20px;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  &:focus-within {
    border-color: #0EA5E9;
    background: #fff;
    color: ${C.textSecondary};
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  outline: none;
  color: ${C.textPrimary};
  &::placeholder {
    color: ${C.textMuted};
  }
`;

const IconBtn = styled.button<{ $active?: boolean }>`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${(p) => (p.$active ? C.bg : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  color: ${C.textSecondary};
  &:hover {
    background: ${C.bg};
    color: ${C.textPrimary};
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: ${C.textPrimary};
  margin-right: 8px;
  &:hover { background: ${C.bg}; }
  @media (max-width: 768px) { display: flex; }
`;

// ─── Main Content ──────────────────────────────────────────────────────────
const MainContent = styled.main`
  flex: 1;
  overflow: auto;
  background: ${C.bg};
`;

// ─── User Panel ────────────────────────────────────────────────────────────
const UserPanelOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: ${(p) => (p.$open ? 'rgba(0,0,0,0.18)' : 'transparent')};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  z-index: 1000;
  transition: background 0.2s;
`;

const UserPanel = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  max-width: 100%;
  height: 100vh;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0,0,0,0.10);
  z-index: 1001;
  transform: translateX(${(p) => (p.$open ? '0' : '100%')});
  transition: transform 0.25s ease;
  display: flex;
  flex-direction: column;
`;

const UserPanelHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${C.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UserPanelClose = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: ${C.bg};
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: ${C.textSecondary};
  &:hover { background: ${C.border}; }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 0 20px;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #38BDF8, #2563EB);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 600;
`;

const UserName = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: ${C.textPrimary};
`;

const UserEmail = styled.div`
  font-size: 13px;
  color: ${C.textSecondary};
`;

const UserGroupName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6366f1;
  background: #eef2ff;
  border-radius: 4px;
  padding: 2px 8px;
  margin-top: 2px;
`;

const UserMenu = styled.nav`
  flex: 1;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: ${(p) => (p.$danger ? '#dc2626' : C.textPrimary)};
  text-align: left;
  &:hover { background: ${C.bg}; }
`;

// ─── Component ─────────────────────────────────────────────────────────────
type AppShellProps = {
  children: React.ReactNode;
  profile: { first_name?: string; last_name?: string; email?: string; username?: string; group_name?: string | null } | null;
  isAllowedAdmin?: boolean;
  isPatientSession?: boolean;
};

type NavItem = { to: string; label: string; icon: React.ReactNode };

const AppShell: React.FC<AppShellProps> = ({ children, profile, isAllowedAdmin, isPatientSession }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const brand = useBrand();
  const [searchQuery, setSearchQuery] = useState('');
  const [userPanelOpen, setUserPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userPanelOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setUserPanelOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [userPanelOpen]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sidebarOpen]);

  const handleSignOut = () => {
    api.logout().catch(() => {}).finally(() => {
      clearAuthToken();
      dispatch(logout());
      setUserPanelOpen(false);
      navigate('/');
    });
  };

  const initials = profile
    ? [profile.first_name?.[0], profile.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?'
    : '?';

  const isNeuroIQ = brand === 'neuroiq';

  const profileItems: NavItem[] = [
    { to: '/modalities/biomarker', label: 'Biomarker Profile', icon: <FlaskIcon /> },
    { to: '/modalities/symptoms',  label: 'Symptom Profile',   icon: <ClipboardIcon /> },
    { to: '/modalities/functional',label: 'Functional Profile',icon: <ActivityIcon /> },
    { to: '/modalities/imaging',   label: 'Imaging Profile',   icon: <ScanIcon /> },
  ];

  const sportsItems: NavItem[] = [
    { to: '/neuroiq/golf',     label: 'Golf',     icon: <GolfIcon /> },
    { to: '/neuroiq/football', label: 'Football', icon: <FootballIcon /> },
    { to: '/neuroiq/baseball', label: 'Baseball', icon: <BaseballIcon /> },
    { to: '/neuroiq/hockey',   label: 'Hockey',   icon: <HockeyIcon /> },
  ];

  const handleNavClick = () => setSidebarOpen(false);

  return (
    <>
      <Layout className={SHELL_CLS.layout}>
        <MobileOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />
        <Sidebar aria-label="Main navigation" className={SHELL_CLS.sidebar} $mobileOpen={sidebarOpen}>
          <SidebarLogoArea>
            <LogoPill>
              {isNeuroIQ
                ? <LogoNeuroIQ width={28} height={28} />
                : <LogoDeep width={28} height={28} />}
            </LogoPill>
            <SidebarBrandName>{isNeuroIQ ? 'NeuroIQ' : 'DrSchmoe AI'}</SidebarBrandName>
          </SidebarLogoArea>

          <SidebarSection>
            <SidebarLink to={isNeuroIQ ? '/neuroiq' : '/dashboard'} end onClick={handleNavClick}>
              <HomeIcon />
              {isNeuroIQ ? 'NeuroIQ Hub' : 'Dashboard'}
            </SidebarLink>
          </SidebarSection>

          <SidebarDivider />

          {isNeuroIQ ? (
            <>
              <SidebarSection>
                <SidebarSectionLabel>Sports Profile</SidebarSectionLabel>
                {sportsItems.map((item) => (
                  <SidebarLink key={item.to} to={item.to} onClick={handleNavClick}>
                    {item.icon}
                    {item.label}
                  </SidebarLink>
                ))}
              </SidebarSection>
              {!isPatientSession && (
                <>
                  <SidebarDivider />
                  <SidebarSection>
                    <SidebarSectionLabel>Bio Profile</SidebarSectionLabel>
                    {profileItems.map((item) => (
                      <SidebarLink key={item.to} to={item.to} onClick={handleNavClick}>
                        {item.icon}
                        {item.label}
                      </SidebarLink>
                    ))}
                  </SidebarSection>
                </>
              )}
            </>
          ) : (
            !isPatientSession && (
              <SidebarSection>
                {profileItems.map((item) => (
                  <SidebarLink key={item.to} to={item.to} onClick={handleNavClick}>
                    {item.icon}
                    {item.label}
                  </SidebarLink>
                ))}
              </SidebarSection>
            )
          )}

          <SidebarSpacer />
          <SidebarDivider />

          {!isPatientSession && (
            <SidebarSection>
              <SidebarLink to="/" end onClick={handleNavClick}>
                <ChatBubbleIcon />
                Ask Dr Schmoe
              </SidebarLink>
            </SidebarSection>
          )}
          <AppVersionBadge />
        </Sidebar>

        <RightArea className={SHELL_CLS.rightArea}>
          <TopBar className={SHELL_CLS.topBar}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <HamburgerBtn type="button" onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle navigation">
                <HamburgerIcon />
              </HamburgerBtn>
              <TopBarBrand />
            </div>
            <RightGroup>
              <SearchWrap>
                <SearchIcon />
                <SearchInput
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search"
                />
              </SearchWrap>
              <IconBtn type="button" onClick={() => navigate('/feedback')} title="Feedback">
                <CommentIcon />
              </IconBtn>
              <IconBtn type="button" onClick={() => {}} title="Notifications">
                <BellIcon />
              </IconBtn>
              <IconBtn
                type="button"
                $active={userPanelOpen}
                onClick={() => setUserPanelOpen(true)}
                title="User account"
                aria-label="Open user menu"
              >
                <Avatar style={{ width: 34, height: 34, fontSize: 13, borderRadius: 10 }}>{initials}</Avatar>
              </IconBtn>
            </RightGroup>
          </TopBar>

          <MainContent className={SHELL_CLS.main}>{children}</MainContent>
        </RightArea>
      </Layout>

      <UserPanelOverlay
        className={SHELL_CLS.userOverlay}
        $open={userPanelOpen}
        onClick={() => setUserPanelOpen(false)}
        aria-hidden={!userPanelOpen}
      />
      <UserPanel
        className={SHELL_CLS.userPanel}
        $open={userPanelOpen}
        ref={panelRef}
        role="dialog"
        aria-label="User menu"
      >
        <UserPanelHeader>
          <span style={{ fontWeight: 600, color: C.textPrimary }}>Account</span>
          <UserPanelClose type="button" onClick={() => setUserPanelOpen(false)} aria-label="Close">
            ×
          </UserPanelClose>
        </UserPanelHeader>
        <UserInfo>
          <Avatar>{initials}</Avatar>
          <UserName>{profile?.first_name} {profile?.last_name}</UserName>
          <UserEmail>{profile?.email?.trim() || profile?.username || ''}</UserEmail>
          {profile?.group_name && <UserGroupName>{profile.group_name}</UserGroupName>}
        </UserInfo>
        <UserMenu>
          {!isPatientSession && isAllowedAdmin && (
            <UserMenuItem type="button" onClick={() => { setUserPanelOpen(false); navigate('/admin/portal'); }}>
              Admin Portal
            </UserMenuItem>
          )}
          {!isPatientSession && isAllowedAdmin && (
            <UserMenuItem type="button" onClick={() => { setUserPanelOpen(false); navigate('/marketing/clinical'); }}>
              Marketing Portal
            </UserMenuItem>
          )}
          {!isPatientSession && isAllowedAdmin && (
            <UserMenuItem type="button" onClick={() => { setUserPanelOpen(false); navigate('/research/portal'); }}>
              Research Portal
            </UserMenuItem>
          )}
          {!isPatientSession && (
            <UserMenuItem type="button" onClick={() => { setUserPanelOpen(false); navigate('/settings'); }}>
              <GearIcon />
              Settings
            </UserMenuItem>
          )}
          <UserMenuItem type="button" $danger onClick={handleSignOut}>
            <SignOutIcon />
            Sign Out
          </UserMenuItem>
        </UserMenu>
      </UserPanel>
    </>
  );
};

export default AppShell;
